import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link, Copy, CheckCircle, FileText } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface AdditionalDocumentRequestProps {
  applicationId: string;
  merchantName: string;
  onLinkGenerated?: (link: string) => void;
}

export function AdditionalDocumentRequest({ 
  applicationId, 
  merchantName,
  onLinkGenerated 
}: AdditionalDocumentRequestProps) {
  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);

  const documentOptions = [
    { value: 'bank_statements', label: 'Bank Statements' },
    { value: 'tax_returns', label: 'Tax Returns' },
    { value: 'business_license', label: 'Business License' },
    { value: 'drivers_license', label: "Driver's License" },
    { value: 'voided_check', label: 'Voided Check' },
    { value: 'processing_statements', label: 'Processing Statements' },
    { value: 'financial_statements', label: 'Financial Statements' },
    { value: 'lease_agreement', label: 'Lease Agreement' },
    { value: 'articles_of_incorporation', label: 'Articles of Incorporation' },
    { value: 'other', label: 'Other Documents' },
  ];

  const generateShareableLink = () => {
    // Generate a unique token for this request
    const requestToken = btoa(`${applicationId}-${Date.now()}`);
    
    // Build the shareable link with query parameters
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      token: requestToken,
      applicationId: applicationId,
      requestedDocs: documentTypes.join(','),
      message: btoa(customMessage) // Encode message to handle special characters
    });
    
    const link = `${baseUrl}/app/merchant-application/upload-documents?${params.toString()}`;
    
    setGeneratedLink(link);
    onLinkGenerated?.(link);
    
    // Store the request details in localStorage (in production, this should be in the database)
    const requestDetails = {
      applicationId,
      merchantName,
      documentTypes,
      customMessage,
      requestedAt: new Date().toISOString(),
      link
    };
    
    const existingRequests = JSON.parse(localStorage.getItem('documentRequests') || '[]');
    existingRequests.push(requestDetails);
    localStorage.setItem('documentRequests', JSON.stringify(existingRequests));
    
    toast({
      title: 'Link Generated',
      description: 'The document request link has been created successfully',
    });
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      toast({
        title: 'Link Copied',
        description: 'The link has been copied to your clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: 'Copy Failed',
        description: 'Failed to copy link. Please select and copy manually.',
        variant: 'destructive',
      });
    }
  };

  const handleDocumentTypeToggle = (type: string) => {
    setDocumentTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const sendEmailWithLink = () => {
    const subject = encodeURIComponent(`Additional Documents Required - ${merchantName}`);
    const body = encodeURIComponent(
      `Dear ${merchantName},\n\n` +
      `We require additional documents to complete your merchant application.\n\n` +
      `${customMessage ? `${customMessage}\n\n` : ''}` +
      `Please upload the requested documents using the following secure link:\n` +
      `${generatedLink}\n\n` +
      `This link will allow you to upload documents directly to your application.\n\n` +
      `Thank you,\n` +
      `The Underwriting Team`
    );
    
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowDialog(true)}
        className="gap-2"
      >
        <FileText className="h-4 w-4" />
        Request Additional Documents
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Request Additional Documents</DialogTitle>
            <DialogDescription>
              Generate a secure link to request specific documents from {merchantName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Document Type Selection */}
            <div>
              <Label>Select Required Documents</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {documentOptions.map(option => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={option.value}
                      checked={documentTypes.includes(option.value)}
                      onChange={() => handleDocumentTypeToggle(option.value)}
                      className="rounded border-gray-300"
                    />
                    <Label 
                      htmlFor={option.value} 
                      className="text-sm font-normal cursor-pointer"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Message */}
            <div>
              <Label htmlFor="message">Custom Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Add any specific instructions or requirements..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={3}
                className="mt-1"
              />
            </div>

            {/* Generated Link Display */}
            {generatedLink && (
              <div className="space-y-2">
                <Label>Generated Link</Label>
                <div className="flex gap-2">
                  <Input
                    value={generatedLink}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={copyToClipboard}
                  >
                    {copied ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                
                <Alert>
                  <AlertDescription>
                    This link will direct the customer to a secure upload page where they can submit the requested documents. 
                    The link remains active even for submitted applications.
                  </AlertDescription>
                </Alert>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={sendEmailWithLink}
                    className="gap-2"
                  >
                    <Link className="h-4 w-4" />
                    Send via Email
                  </Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDialog(false);
                setDocumentTypes([]);
                setCustomMessage('');
                setGeneratedLink('');
              }}
            >
              Close
            </Button>
            {!generatedLink && (
              <Button 
                onClick={generateShareableLink}
                disabled={documentTypes.length === 0}
              >
                Generate Link
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}